import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography
} from 'antd';
import { EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { teacherResourceAPI } from '../services/api';

const { Title, Text } = Typography;
const { TextArea, Search } = Input;

const TOPICS = {
  安全: ['防火防溺水', '校园欺凌', '防诈骗', '突发事件', '交通安全'],
  纪律: ['考勤请假', '课堂纪律', '违纪记录', '谈话跟进'],
  学习: ['作业情况', '课堂表现', '成绩变化', '学习辅导'],
  健康: ['身体健康', '心理健康', '特殊体质', '重点关怀'],
  卫生: ['教室卫生', '学生卫生', '值日检查', '整改记录']
};

const RESOURCE_TYPES = ['操作手册', '案例', '可用资料', '模板', '政策法规', '官方网站'];
const STATUS_META = {
  draft: { label: '草稿', color: 'default' },
  published: { label: '已发布', color: 'success' },
  disabled: { label: '已下架', color: 'error' }
};

function toFormValues(record) {
  if (!record) {
    return {
      libraryCode: 'teacher_work',
      groupName: '纪律',
      topicName: '考勤请假',
      resourceType: '模板',
      actionType: 'copy',
      actionLabel: '复制内容',
      versionLabel: '1.0',
      status: 'draft',
      sortOrder: 0
    };
  }
  return {
    libraryCode: record.library_code,
    groupName: record.group_name,
    topicName: record.topic_name,
    resourceType: record.resource_type,
    title: record.title,
    summary: record.summary,
    content: record.content || '',
    actionType: record.action_type,
    actionLabel: record.action_label || '',
    targetUrl: record.target_url || '',
    sourceName: record.source_name || '',
    sourceUrl: record.source_url || '',
    versionLabel: record.version_label || '1.0',
    status: record.status,
    sortOrder: Number(record.sort_order || 0)
  };
}

function TeacherResourceManagement() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(undefined);
  const [groupName, setGroupName] = useState(undefined);
  const [keyword, setKeyword] = useState('');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const selectedGroup = Form.useWatch('groupName', form) || '纪律';
  const actionType = Form.useWatch('actionType', form) || 'copy';

  const loadRecords = async (nextKeyword = keyword) => {
    try {
      setLoading(true);
      const response = await teacherResourceAPI.getAll({
        status,
        groupName,
        keyword: nextKeyword.trim() || undefined
      });
      setRecords(response.data.data || []);
    } catch (error) {
      message.error(error.response?.data?.message || '加载教师资源失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, [status, groupName]);

  const openEditor = (record = null) => {
    setEditing(record || { id: null });
    form.setFieldsValue(toFormValues(record));
  };

  const handleGroupChange = (value) => {
    form.setFieldValue('topicName', TOPICS[value][0]);
  };

  const saveResource = async (values) => {
    try {
      setSaving(true);
      const payload = {
        ...values,
        content: values.content || '',
        targetUrl: values.targetUrl || '',
        sourceName: values.sourceName || '',
        sourceUrl: values.sourceUrl || '',
        actionLabel: values.actionLabel || ''
      };
      if (editing?.id) await teacherResourceAPI.update(editing.id, payload);
      else await teacherResourceAPI.create(payload);
      message.success(editing?.id ? '资源已更新' : '资源已创建');
      setEditing(null);
      form.resetFields();
      loadRecords();
    } catch (error) {
      message.error(error.response?.data?.message || '保存教师资源失败');
    } finally {
      setSaving(false);
    }
  };

  const topicCount = useMemo(() => new Set(records.map((item) => item.topic_name)).size, [records]);

  const columns = [
    {
      title: '专题',
      key: 'topic',
      width: 150,
      render: (_, record) => <><div>{record.topic_name}</div><Text type="secondary">{record.group_name}</Text></>
    },
    { title: '类型', dataIndex: 'resource_type', width: 100, render: (value) => <Tag>{value}</Tag> },
    {
      title: '资源内容',
      key: 'resource',
      render: (_, record) => <><div style={{ fontWeight: 600 }}>{record.title}</div><Text type="secondary">{record.summary}</Text></>
    },
    {
      title: '使用方式',
      dataIndex: 'action_type',
      width: 110,
      render: (value) => ({ copy: '复制使用', internal_link: '站内入口', external_link: '外部链接' }[value] || value)
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (value) => <Tag color={STATUS_META[value]?.color}>{STATUS_META[value]?.label || value}</Tag>
    },
    {
      title: '版本/更新',
      key: 'version',
      width: 145,
      render: (_, record) => <><div>v{record.version_label || '1.0'}</div><Text type="secondary">{dayjs(record.updated_at).format('YYYY-MM-DD HH:mm')}</Text></>
    },
    {
      title: '操作',
      key: 'actions',
      width: 90,
      fixed: 'right',
      render: (_, record) => <Button type="link" icon={<EditOutlined />} onClick={() => openEditor(record)}>编辑</Button>
    }
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>教师资源库管理</Title>
            <Text type="secondary">管理教师工作库的完整手册、模板、可复制资料和权威入口。</Text>
          </div>
          <Alert showIcon type="info" message={`当前列表 ${records.length} 份资源，覆盖 ${topicCount} 个专题。只有“已发布”资源会在导航首页展示。`} />
          <Space wrap>
            <Select
              allowClear
              placeholder="全部状态"
              value={status}
              onChange={setStatus}
              style={{ width: 130 }}
              options={Object.entries(STATUS_META).map(([value, item]) => ({ value, label: item.label }))}
            />
            <Select
              allowClear
              placeholder="全部分组"
              value={groupName}
              onChange={setGroupName}
              style={{ width: 130 }}
              options={Object.keys(TOPICS).map((value) => ({ value, label: value }))}
            />
            <Search
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              onSearch={(value) => loadRecords(value)}
              placeholder="搜索标题、摘要或专题"
              style={{ width: 280 }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => loadRecords()}>刷新</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor()}>新增资源</Button>
          </Space>
        </Space>
      </Card>

      <Table
        rowKey={(record) => String(record.id)}
        columns={columns}
        dataSource={records}
        loading={loading}
        scroll={{ x: 1050 }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />

      <Modal
        title={editing?.id ? '编辑教师资源' : '新增教师资源'}
        open={Boolean(editing)}
        onCancel={() => setEditing(null)}
        footer={null}
        width={820}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={saveResource} preserve={false}>
          <Form.Item name="libraryCode" hidden><Input /></Form.Item>
          <Space align="start" wrap style={{ width: '100%' }}>
            <Form.Item name="groupName" label="分组" rules={[{ required: true }]}>
              <Select style={{ width: 150 }} options={Object.keys(TOPICS).map((value) => ({ value, label: value }))} onChange={handleGroupChange} />
            </Form.Item>
            <Form.Item name="topicName" label="专题" rules={[{ required: true }]}>
              <Select style={{ width: 180 }} options={(TOPICS[selectedGroup] || []).map((value) => ({ value, label: value }))} />
            </Form.Item>
            <Form.Item name="resourceType" label="资源类型" rules={[{ required: true }]}>
              <Select style={{ width: 150 }} options={RESOURCE_TYPES.map((value) => ({ value, label: value }))} />
            </Form.Item>
            <Form.Item name="status" label="状态" rules={[{ required: true }]}>
              <Select style={{ width: 130 }} options={Object.entries(STATUS_META).map(([value, item]) => ({ value, label: item.label }))} />
            </Form.Item>
          </Space>
          <Form.Item name="title" label="资源标题" rules={[{ required: true, min: 2 }]}><Input maxLength={160} /></Form.Item>
          <Form.Item name="summary" label="使用说明" rules={[{ required: true, min: 5 }]}><TextArea rows={2} maxLength={500} /></Form.Item>
          <Space align="start" wrap style={{ width: '100%' }}>
            <Form.Item name="actionType" label="使用方式" rules={[{ required: true }]}>
              <Select style={{ width: 160 }} options={[
                { value: 'copy', label: '复制使用' },
                { value: 'internal_link', label: '小喇叭站内入口' },
                { value: 'external_link', label: '外部权威入口' }
              ]} />
            </Form.Item>
            <Form.Item name="actionLabel" label="按钮名称"><Input style={{ width: 180 }} maxLength={40} /></Form.Item>
            <Form.Item name="versionLabel" label="版本" rules={[{ required: true }]}><Input style={{ width: 110 }} maxLength={40} /></Form.Item>
            <Form.Item name="sortOrder" label="排序"><InputNumber style={{ width: 110 }} /></Form.Item>
          </Space>
          {actionType === 'copy' ? (
            <Form.Item name="content" label="可直接使用的完整正文" rules={[{ required: true, message: '请填写可直接复制使用的正文' }]}>
              <TextArea rows={12} maxLength={50000} showCount />
            </Form.Item>
          ) : (
            <Form.Item name="targetUrl" label="目标链接" rules={[{ required: true, message: '请填写目标链接' }]}>
              <Input placeholder="站内 /path 或 https://" />
            </Form.Item>
          )}
          <Space align="start" wrap style={{ width: '100%' }}>
            <Form.Item name="sourceName" label="来源名称"><Input style={{ width: 250 }} maxLength={160} /></Form.Item>
            <Form.Item name="sourceUrl" label="来源网址"><Input style={{ width: 360 }} placeholder="https://" /></Form.Item>
          </Space>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setEditing(null)}>取消</Button>
            <Button type="primary" htmlType="submit" loading={saving}>保存</Button>
          </Space>
        </Form>
      </Modal>
    </Space>
  );
}

export default TeacherResourceManagement;
